import React, { useState, useEffect } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import TimeInput from "./TimeInput";
import { SaveButton, CloseButton } from "../../../common/Buttons";

function CreateProcessDialog({ table, itemData, handleCreateProcess }) {
  const [time, setTime] = useState({ hours: "", minutes: "", seconds: "" });
  const [newProc, setNewProc] = useState({
    proc_name: "",
    batch_size: 1,
    uph: 0,
  });

  // 어떤 필드가 마지막으로 수정되었는지 추적
  const [lastUpdated, setLastUpdated] = useState(null);

  // 공정 시간을 초 단위로 변환하는 함수
  const calculateTotalSeconds = () => {
    return (
      Number(time.hours || 0) * 3600 +
      Number(time.minutes || 0) * 60 +
      Number(time.seconds || 0)
    );
  };

  // UPH가 변경되면 시간 업데이트
  useEffect(() => {
    if (
      lastUpdated !== "uph" ||
      !newProc.uph ||
      newProc.uph <= 0 ||
      !newProc.batch_size
    )
      return;

    // UPH로부터 초 단위 시간 계산
    const totalSeconds = Math.round((3600 / newProc.uph) * newProc.batch_size);

    // 시, 분, 초로 변환
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // 두 자릿수 형식으로 변환
    setTime({
      hours: hours.toString().padStart(2, "0"),
      minutes: minutes.toString().padStart(2, "0"),
      seconds: seconds.toString().padStart(2, "0"),
    });
  }, [newProc.uph, lastUpdated]);

  // 시간이 변경되면 UPH 업데이트
  useEffect(() => {
    if (lastUpdated !== "time") return;

    const totalSeconds = calculateTotalSeconds();
    if (totalSeconds <= 0 || !newProc.batch_size) return;

    // 공정 시간으로부터 UPH 계산
    const calculatedUph = (3600 / totalSeconds) * newProc.batch_size;

    // 소수점 둘째 자리까지 반올림
    const roundedUph = Math.round(calculatedUph * 100) / 100;

    setNewProc((prev) => ({
      ...prev,
      uph: roundedUph,
    }));
  }, [time, lastUpdated]);

  const handleCreate = () => {
    const totalSeconds = calculateTotalSeconds();
    if (!newProc.proc_name) {
      toast.error("공정명을 입력해 주세요.");
      return;
    }
    if (!newProc.batch_size || newProc.batch_size <= 0) {
      toast.error("배치 사이즈는 1 이상 입력해 주세요.");
      return;
    }
    if (totalSeconds <= 0 && newProc.uph <= 0) {
      toast.error("공정 시간 또는 UPH를 입력해 주세요.");
      return;
    }

    handleCreateProcess({
      values: {
        proc_name: newProc.proc_name,
        proc_time: totalSeconds,
        batch_size: newProc.batch_size,
        uph: newProc.uph,
      },
      table: table,
    });
  };

  const handleInputChange = (event, field) => {
    let value = event.target.value;

    if (field === "batch_size") {
      value = Number(value);
      if (value <= 0) value = "";
    } else if (field === "uph") {
      value = Number(value);
      if (value < 0) value = 0;
      setLastUpdated("uph");
    }

    setNewProc((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // 시간 변경 핸들러
  const handleTimeChange = (newTime) => {
    setTime(newTime);
    setLastUpdated("time");
  };

  return (
    <>
      <DialogTitle variant="h3">공정 추가</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: "1.6rem" }}
      >
        <TextField
          label="공정명"
          fullWidth
          required
          value={newProc.proc_name}
          onChange={(e) => handleInputChange(e, "proc_name")}
        />
        <TimeInput time={time} setTime={handleTimeChange} />
        <TextField
          label="UPH"
          fullWidth
          required
          type="number"
          inputProps={{ step: "0.01", min: "0" }}
          value={newProc.uph}
          onChange={(e) => handleInputChange(e, "uph")}
        />
        <TextField
          label="배치사이즈"
          fullWidth
          required
          type="number"
          inputProps={{ min: "1" }}
          value={newProc.batch_size}
          onChange={(e) => handleInputChange(e, "batch_size")}
        />
      </DialogContent>
      <DialogActions>
        <SaveButton onClick={handleCreate} variant="contained" />
        <CloseButton onClick={() => table.setCreatingRow(null)} />
      </DialogActions>
    </>
  );
}

export default CreateProcessDialog;
